import React from 'react';
import { Search, Inbox, Zap, BotOff, Settings, SquarePen } from 'lucide-react';
import {
  Conversation,
  ConversationFilters,
  canSendFreeform
} from '../../../types/models/whatsapp';

interface ConversationListProps {
  conversations: Conversation[];
  selectedId: string | null;
  loading: boolean;
  filters: ConversationFilters;
  onSelect: (id: string) => void;
  onFiltersChange: (filters: Partial<ConversationFilters>) => void;
  onManageCannedReplies?: () => void;
  onOpenSettings?: () => void;
  onNewChat?: () => void;
}

const formatRelative = (timestamp: number): string => {
  if (!timestamp) return '';
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(timestamp).toLocaleDateString([], {
    day: 'numeric',
    month: 'short'
  });
};

const initialsOf = (name: string, phone: string): string => {
  const source = name.trim() || phone;
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
};

// STATUS_TABS and FilterChip were removed when the filters moved to the
// full-width top tab row — the tab row renders them inline.

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  selectedId,
  loading,
  filters,
  onSelect,
  onFiltersChange,
  onManageCannedReplies,
  onOpenSettings,
  onNewChat
}) => {
  // 'Clear' appears only when something is actually filtered — status is
  // excluded because it always has a value.
  const hasActiveFilters =
    (filters.assigneeId && filters.assigneeId !== 'all') ||
    filters.unreadOnly === true ||
    (filters.botState && filters.botState !== 'all') ||
    (filters.windowState && filters.windowState !== 'all') ||
    (filters.tags?.length ?? 0) > 0;

  return (
    <div className="flex h-full w-full flex-col border-r border-wa-border bg-wa-surface">
      <div className="border-b border-wa-border p-3">
        {onNewChat && (
          <button
            type="button"
            onClick={onNewChat}
            className="mb-3 flex w-full items-center justify-center gap-2 rounded-lg bg-wa-green px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-wa-teal"
          >
            <SquarePen className="h-4 w-4" />
            New chat
          </button>
        )}

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={filters.search || ''}
              onChange={(e) => onFiltersChange({ search: e.target.value })}
              placeholder="Search name or phone"
              className="w-full rounded-lg border-wa-border bg-wa-surface2 pl-9 text-sm text-wa-text focus:border-wa-green focus:ring-wa-green"
            />
          </div>

          {onManageCannedReplies && (
            <button
              type="button"
              onClick={onManageCannedReplies}
              className="shrink-0 rounded-lg p-2 text-wa-muted transition-colors hover:bg-wa-surface2 hover:text-wa-text"
              aria-label="Manage canned replies"
              title="Manage canned replies"
            >
              <Zap className="h-4 w-4" />
            </button>
          )}

          {onOpenSettings && (
            <button
              type="button"
              onClick={onOpenSettings}
              className="shrink-0 rounded-lg p-2 text-wa-muted transition-colors hover:bg-wa-surface2 hover:text-wa-text"
              aria-label="WhatsApp settings"
              title="WhatsApp settings"
            >
              <Settings className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Full-width top filter row via absolute positioning to the parent WhatsAppInbox */}
        <div className="absolute top-0 left-0 right-0 flex h-12 items-center gap-6 overflow-x-auto border-b border-wa-border bg-wa-bg px-4 z-10 whitespace-nowrap">
          <button
            type="button"
            onClick={() => onFiltersChange({ status: 'open' })}
            className={`flex h-full items-center border-b-2 text-sm font-medium transition-colors ${
              filters.status === 'open' ? 'border-wa-green text-wa-green' : 'border-transparent text-wa-text hover:text-white'
            }`}
          >
            Open Chats
          </button>
          <button
            type="button"
            onClick={() => onFiltersChange({ status: 'resolved' })}
            className={`flex h-full items-center border-b-2 text-sm font-medium transition-colors ${
              filters.status === 'resolved' ? 'border-wa-green text-wa-green' : 'border-transparent text-wa-text hover:text-white'
            }`}
          >
            Closed Chats
          </button>
          <button
            type="button"
            onClick={() => onFiltersChange({ unreadOnly: !filters.unreadOnly })}
            className={`flex h-full items-center border-b-2 text-sm font-medium transition-colors ${
              filters.unreadOnly ? 'border-wa-green text-wa-green' : 'border-transparent text-wa-text hover:text-white'
            }`}
          >
            Unread Chats
          </button>
          <button
            type="button"
            onClick={() => onFiltersChange({ assigneeId: filters.assigneeId === 'me' ? 'all' : 'me' })}
            className={`flex h-full items-center border-b-2 text-sm font-medium transition-colors ${
              filters.assigneeId === 'me' ? 'border-wa-green text-wa-green' : 'border-transparent text-wa-text hover:text-white'
            }`}
          >
            Assigned Chats
          </button>
          <button
            type="button"
            onClick={() => onFiltersChange({ assigneeId: filters.assigneeId === 'unassigned' ? 'all' : 'unassigned' })}
            className={`flex h-full items-center border-b-2 text-sm font-medium transition-colors ${
              filters.assigneeId === 'unassigned' ? 'border-wa-green text-wa-green' : 'border-transparent text-wa-text hover:text-white'
            }`}
          >
            Unassigned Chats
          </button>
          <button
            type="button"
            onClick={() => onFiltersChange({ botState: filters.botState === 'on' ? 'all' : 'on' })}
            className={`flex h-full items-center border-b-2 text-sm font-medium transition-colors ${
              filters.botState === 'on' ? 'border-wa-green text-wa-green' : 'border-transparent text-wa-text hover:text-white'
            }`}
          >
            Bot On
          </button>
          <button
            type="button"
            onClick={() => onFiltersChange({ botState: filters.botState === 'off' ? 'all' : 'off' })}
            className={`flex h-full items-center border-b-2 text-sm font-medium transition-colors ${
              filters.botState === 'off' ? 'border-wa-green text-wa-green' : 'border-transparent text-wa-text hover:text-white'
            }`}
          >
            Bot Off
          </button>
          <button
            type="button"
            className="flex h-full items-center border-b-2 border-transparent text-sm font-medium text-wa-text hover:text-white transition-colors"
          >
            Add Tags
          </button>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={() =>
                onFiltersChange({
                  status: 'open',
                  assigneeId: 'all',
                  unreadOnly: false,
                  botState: 'all',
                  windowState: 'all',
                  tags: []
                })
              }
              className="flex h-full items-center text-sm font-medium text-danger-500 hover:text-danger-400 transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="space-y-3 p-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex animate-pulse gap-3">
                <div className="h-10 w-10 rounded-full bg-gray-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-1/2 rounded bg-gray-200" />
                  <div className="h-3 w-3/4 rounded bg-gray-200" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && conversations.length === 0 && (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <Inbox className="h-10 w-10 text-gray-300" />
            <p className="mt-2 text-sm font-medium text-gray-500">
              No conversations
            </p>
            <p className="mt-1 text-xs text-gray-400">
              Incoming WhatsApp messages will appear here.
            </p>
          </div>
        )}

        {!loading &&
          conversations.map((conversation) => {
            const isSelected = conversation.id === selectedId;
            const windowOpen = canSendFreeform(conversation);

            return (
              <button
                key={conversation.id}
                type="button"
                onClick={() => onSelect(conversation.id)}
                className={`flex w-full items-start gap-3 border-b border-wa-border p-3 text-left transition-colors ${
                  isSelected
                    ? 'bg-wa-surface2'
                    : 'hover:bg-wa-surface2/50'
                }`}
              >
                <div className="relative shrink-0">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-wa-teal text-sm font-semibold text-white">
                    {initialsOf(conversation.contactName, conversation.contactPhone)}
                  </span>
                  {windowOpen && (
                    <span
                      className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-wa-surface bg-wa-green"
                      title="24h window open"
                    />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-sm font-medium text-wa-text">
                      {conversation.contactName || conversation.contactPhone}
                    </span>
                    <span className="shrink-0 text-[11px] text-wa-muted">
                      {formatRelative(conversation.lastMessageAt)}
                    </span>
                  </div>

                  <div className="mt-0.5 flex items-center justify-between gap-2">
                    <span className="truncate text-xs text-wa-muted">
                      {conversation.lastMessagePreview || 'No messages yet'}
                    </span>
                    {conversation.unreadCount > 0 && (
                      <span className="flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-wa-green px-1.5 text-[11px] font-semibold text-white">
                        {conversation.unreadCount}
                      </span>
                    )}
                  </div>

                  {(conversation.assigneeName ||
                    conversation.tags.length > 0 ||
                    !conversation.botEnabled) && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-1">
                      {/* Only bot-OFF is badged: on is the default, and badging
                          every row would be noise. */}
                      {!conversation.botEnabled && (
                        <span
                          className="inline-flex items-center gap-0.5 rounded bg-wa-surface2 px-1.5 py-0.5 text-[10px] text-wa-muted"
                          title="Automation is off for this chat"
                        >
                          <BotOff className="h-2.5 w-2.5" />
                          Bot off
                        </span>
                      )}
                      {conversation.assigneeName && (
                        <span className="rounded bg-wa-surface2 px-1.5 py-0.5 text-[10px] text-wa-muted">
                          {conversation.assigneeName}
                        </span>
                      )}
                      {conversation.tags.slice(0, 2).map((tag) => (
                        <span
                          key={tag}
                          className="rounded bg-wa-surface2 px-1.5 py-0.5 text-[10px] text-wa-text"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
      </div>
    </div>
  );
};

export default ConversationList;
